import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [readingMode, setReadingMode] = useState(false);

  const comic = comics.find((c) => c.id === id);
  const categoryInfo = categories.find((c) => c.slug === comic?.category);
  const relatedComics = comics.filter((c) => c.id !== id && c.category === comic?.category).slice(0, 4);

  useEffect(() => {
    window.scrollTo(0, 0);
    setReadingMode(false);
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-foreground-700 mb-4">漫画未找到</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  if (readingMode) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 bg-background-50">
          <div className="w-full px-4 md:px-8 py-6">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setReadingMode(false)}
                  className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" /> 返回详情
                </button>
                <span className="text-sm text-foreground-500">{comic.title}</span>
              </div>

              <div className="space-y-6">
                {comic.chapters.map((ch) => (
                  <article key={ch.id} className="bg-background-100 rounded-lg p-6 md:p-8">
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">{ch.title}</h3>
                    <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                      <p>本章内容为{comic.title}的第{ch.number}话。在哔咔动漫平台，你可以享受流畅的下拉式阅读体验，无需翻页，手指轻轻下滑即可连续阅读全部章节。</p>
                      <p>本页面为漫画详情阅读展示页面，完整漫画内容请通过哔咔漫画免费观看入口访问。平台提供日漫韩漫国漫全免费下拉式在线阅读服务，海量资源零付费限制。</p>
                      <p>如果你喜欢{comic.title}，欢迎将哔咔动漫分享给更多漫画爱好者。我们的免费漫画大全持续更新中，每日都有新内容上线。</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={() => setReadingMode(false)}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  返回漫画详情
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="w-full px-4 md:px-8 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: categoryInfo?.name || '漫画', href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <div className="mt-8 flex flex-col lg:flex-row gap-8 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 w-full sm:w-56 md:w-64">
                <div className="aspect-[5/7] w-full rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    width="400"
                    height="560"
                  />
                </div>
                <button
                  onClick={() => setReadingMode(true)}
                  className="w-full mt-4 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line mr-1.5" />
                  {t('comic.startReading')}
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-5 mb-5 text-sm">
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-user-line text-foreground-400" />
                    <span className="text-foreground-500">{t('comic.author')}：</span>
                    <strong className="font-medium text-foreground-800">{comic.author}</strong>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-flag-2-line text-foreground-400" />
                    <span className="text-foreground-500">{t('comic.status')}：</span>
                    <span className={`font-medium ${comic.status === '完结' ? 'text-secondary-600' : 'text-accent-600'}`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-time-line text-foreground-400" />
                    <span className="text-foreground-500">{t('comic.updateTime')}：</span>
                    <span className="text-foreground-800">{comic.updateTime}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-eye-line text-foreground-400" />
                    <span className="text-foreground-500">{t('comic.views')}：</span>
                    <span className="text-foreground-800">{formatViews(comic.views)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-600">
                    <i className="ri-star-fill text-accent-500" />
                    <span className="text-foreground-500">{t('comic.rating')}：</span>
                    <span className="font-bold text-accent-600">{comic.rating}</span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="mb-8">
                  <h2 className="text-base font-heading font-bold text-foreground-900 mb-2">{t('comic.description')}</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                {/* Chapters */}
                <div>
                  <h2 className="text-base font-heading font-bold text-foreground-900 mb-3">
                    {t('comic.chapters')}
                    <span className="text-sm font-normal text-foreground-500 ml-2">
                      {t('comic.chapterCount', { count: comic.chapters.length })}
                    </span>
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {comic.chapters.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => setReadingMode(true)}
                        className="px-4 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors text-left cursor-pointer whitespace-nowrap truncate"
                      >
                        {ch.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Related */}
            {relatedComics.length > 0 && (
              <section className="mt-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  {t('comic.relatedRecommend')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} {...rc} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${comic.title} - 哔咔动漫`,
            description: comic.description,
            author: { "@type": "Person", name: comic.author },
            datePublished: comic.updateTime,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://bikadongman.com/" },
                { "@type": "ListItem", position: 2, name: categoryInfo?.name || '漫画', item: `https://bikadongman.com/category/${comic.category}` },
                { "@type": "ListItem", position: 3, name: comic.title, item: `https://bikadongman.com/comic/${comic.id}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}